/**
 * ContentReferenceSection - Wrapper component for content reference management
 * @module content-detail/content-references/ContentReferenceSection
 */
import React from 'react'
import { Box, Container, Grid, Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import ContentReferenceAutocomplete from './ContentReferenceAutocomplete'
import type { ContentReference } from '../types'

interface ContentReferenceSectionProps {
  /** Label to display on the left (e.g., "Admin Category") */
  label: string
  /** Selected content references */
  value: ContentReference[]
  /** Called when selection changes */
  onChange: (newValue: ContentReference[]) => void
  /** Whether field is disabled (view-only mode) */
  disabled: boolean
  /** Content type to search (e.g., "tag", "person") */
  contentType: string
  /** Bundle to filter by (optional) */
  bundle?: string
  /** Filter type (optional, alternative to bundle) */
  filterType?: string
  /** Whether to allow multiple selections (false = single-select, e.g. Series, Admin Category) */
  multiple?: boolean
  /** Optional tooltip text; when set, an info icon after the label shows this on hover */
  tooltip?: string
}

/**
 * ContentReferenceSection Component
 *
 * Displays a single content reference field with label and autocomplete
 * Uses Grid layout matching video detail page (20% label, 80% field)
 */
function ContentReferenceSection (props: ContentReferenceSectionProps) {
  const {
    label,
    value = [],
    onChange,
    disabled = false,
    contentType,
    bundle,
    filterType,
    multiple = true,
    tooltip,
  } = props

  return (
    <Container maxWidth={false} disableGutters>
      <Grid container spacing={2} alignItems="center">
        {/* Label - 20% width */}
        <Grid item xs={2}>
          <Box component="p" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, m: 0 }}>
            {label}
            {tooltip != null && tooltip !== '' && (
              <Tooltip title={tooltip} placement="top">
                <InfoIcon sx={{ fontSize: 16 }} />
              </Tooltip>
            )}
          </Box>
        </Grid>

        {/* Autocomplete Field - 80% width */}
        <Grid item xs={10}>
          <ContentReferenceAutocomplete
            value={value}
            onChange={onChange}
            contentType={contentType}
            bundle={bundle}
            filterType={filterType}
            label={`Select ${label}`}
            disabled={disabled}
            multiple={multiple}
          />
        </Grid>
      </Grid>
    </Container>
  )
}

export default ContentReferenceSection
